import { BAUSTEINE } from "./bausteine"

/**
 * Connector catalogue for the Compass integrations demo.
 *
 * SIMULATION DATA — no connector here performs network I/O. The shape mirrors
 * the `LmsPort.supports` design from docs/lms-integration.md §5 so the demo
 * shows the real architecture's vocabulary (four surfaces: catalogue,
 * transcript, launch, assignment), and so a later real implementation replaces
 * the simulation without changing what the UI says.
 */

export type ConnectorId = "moodle" | "sf-learning" | "sap-fiori" | "csv" | "lrs"

export type ConnectorAvailability = "connectable" | "available" | "coming"

export interface ConnectorMeta {
    id: ConnectorId
    name: string
    /** Shown under the name — the concrete system, not marketing. */
    detail: string
    availability: ConnectorAvailability
    supports: {
        catalog: boolean
        transcript: boolean
        launch: boolean
        assign: boolean
    }
}

/**
 * Only `moodle` (and, nominally, `csv`) are connectable — the others render as
 * available/coming. That asymmetry is deliberate realism: a real rollout never
 * has every system wired up on day one, so pretending otherwise would be the
 * less convincing demo.
 */
export const CONNECTORS: ConnectorMeta[] = [
    {
        id: "moodle",
        name: "Moodle",
        detail: "learn.acme-bank.ch · Web Services + LTI 1.3",
        availability: "connectable",
        supports: { catalog: true, transcript: true, launch: true, assign: true },
    },
    {
        id: "sf-learning",
        name: "SAP SuccessFactors Learning",
        detail: "Learning OData · learningHistory / learningPlan",
        availability: "available",
        supports: { catalog: true, transcript: true, launch: true, assign: true },
    },
    {
        id: "sap-fiori",
        name: "SAP Fiori / UI5",
        detail: "hrportal.gkb.ch · Deep-Link",
        availability: "available",
        supports: { catalog: false, transcript: false, launch: true, assign: false },
    },
    {
        id: "csv",
        name: "CSV / Excel",
        detail: "Katalog- und Transcript-Import",
        availability: "connectable",
        supports: { catalog: true, transcript: true, launch: false, assign: false },
    },
    {
        id: "lrs",
        name: "xAPI / LRS",
        detail: "Learning Record Store · actor–verb–object",
        availability: "coming",
        supports: { catalog: false, transcript: true, launch: false, assign: false },
    },
]

export const CONNECTOR_BY_ID: ReadonlyMap<ConnectorId, ConnectorMeta> = new Map(
    CONNECTORS.map((connector) => [connector.id, connector]),
)

// ---------------------------------------------------------------------------
// Fake Moodle course-module ids
// ---------------------------------------------------------------------------

/** Deterministic 32-bit hash (FNV-1a) — no Math.random in the demo, ever. */
function fnv1a(input: string): number {
    let hash = 0x811c9dc5
    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }
    return hash >>> 0
}

/**
 * Moodle-shaped `cmid` per Baustein: 3–4 digits, stable across reloads. A pure
 * hash of the id — every course in the catalogue is synthetic, so there is no
 * "real" id left to anchor near.
 */
export function moodleCmid(bausteinId: string): number {
    return 400 + (fnv1a(bausteinId) % 1200)
}

/**
 * The two Bausteine whose auto skill-mapping is flagged "zur Prüfung" in the
 * import step. Hash-picked but pinned here so the wizard copy ("40 automatisch,
 * 2 zur Prüfung") never drifts from what the log shows.
 */
export const REVIEW_FLAGGED_BAUSTEINE: ReadonlySet<string> = new Set(
    [...BAUSTEINE]
        .sort((a, b) => (fnv1a(`review-${a.id}`) % 1000) - (fnv1a(`review-${b.id}`) % 1000))
        .slice(0, 2)
        .map((baustein) => baustein.id),
)

/** Seconds each import line waits — believable, deterministic pacing. */
export function importLineDelayMs(index: number): number {
    return 90 + (fnv1a(`delay-${index}`) % 140)
}
